import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Progress,
  SimpleGrid,
  Spinner,
  Switch,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useDebounceCallback } from 'usehooks-ts';
import { toast } from 'sonner';
import {
  BookOpen,
  Buildings,
  CaretDown,
  CheckCircle,
  Calculator,
  FileText,
  Hourglass,
  MagnifyingGlass,
  PaperPlaneTilt,
  Question,
  Receipt,
  Scales,
  Warning,
  WarningCircle,
  XCircle,
} from 'phosphor-react';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { GlassPageBg } from '../../../../components/glass/GlassPageBg';
import { GlassCard } from '../../../../components/glass/GlassCard';
import { Tour, TourPasso, useTour } from '../../../../components/Tour';
import { useAuth } from '../../../../providers/auth';
import {
  AcaoLote,
  IStatusLote,
  ITotais,
  TELA_FATURAS_PATH,
  TELA_PATH,
  mensagemErro,
  snApi,
} from './api';
import { brl } from './regras';
import { ApuracaoCard } from './ApuracaoCard';
import { ApuracaoDrawer } from './ApuracaoDrawer';
import { MotorCalculoDrawer } from './MotorCalculoDrawer';

const TAMANHO = 50;

interface IKpi {
  filtro: string;
  rotulo: string;
  cor: string;
  icone: React.ReactNode;
  dica: string;
  valor: (t?: ITotais) => number;
}

const KPIS: IKpi[] = [
  {
    filtro: '',
    rotulo: 'Empresas do Simples',
    cor: '#1B88F5',
    icone: <Buildings size={18} weight="duotone" />,
    dica: 'Todas as empresas do Simples da sua carteira',
    valor: t => t?.total_empresas ?? 0,
  },
  {
    filtro: 'nao_calculada',
    rotulo: 'Não calculadas',
    cor: '#94A3B8',
    icone: <Hourglass size={18} weight="duotone" />,
    dica: 'Sem cálculo neste mês',
    valor: t => t?.nao_calculadas ?? 0,
  },
  {
    filtro: 'pendencias',
    rotulo: 'Com pendências',
    cor: '#F59E0B',
    icone: <Warning size={18} weight="duotone" />,
    dica: 'Precisam de conferência antes de aprovar',
    valor: t => t?.com_pendencias ?? 0,
  },
  {
    filtro: 'divergentes',
    rotulo: 'Diferente do Domínio',
    cor: '#EF4444',
    icone: <Scales size={18} weight="duotone" />,
    dica: 'Nosso DAS difere mais de R$ 1 do Domínio',
    valor: t => t?.divergentes_dominio ?? 0,
  },
  {
    filtro: 'calculada',
    rotulo: 'Em conferência',
    cor: '#F59E0B',
    icone: <Calculator size={18} weight="duotone" />,
    dica: 'Calculadas, aguardando aprovação',
    valor: t => t?.por_situacao?.calculada ?? 0,
  },
  {
    filtro: 'aprovada',
    rotulo: 'Aprovadas',
    cor: '#6366F1',
    icone: <CheckCircle size={18} weight="duotone" />,
    dica: 'Prontas para transmitir',
    valor: t => t?.por_situacao?.aprovada ?? 0,
  },
  {
    filtro: 'transmitida',
    rotulo: 'Transmitidas',
    cor: '#14B8A6',
    icone: <PaperPlaneTilt size={18} weight="duotone" />,
    dica: 'Declaração entregue, faltam DAS/extrato',
    valor: t => (t?.por_situacao?.transmitida ?? 0) + (t?.por_situacao?.transmitindo ?? 0),
  },
  {
    filtro: 'concluida',
    rotulo: 'Concluídas',
    cor: '#22C55E',
    icone: <Receipt size={18} weight="duotone" />,
    dica: 'Declaração, recibo, DAS e extrato salvos',
    valor: t => t?.por_situacao?.concluida ?? 0,
  },
  {
    filtro: 'erro',
    rotulo: 'Com erro',
    cor: '#EF4444',
    icone: <XCircle size={18} weight="duotone" />,
    dica: 'O SERPRO recusou',
    valor: t => t?.por_situacao?.erro ?? 0,
  },
];

const ACOES_LOTE: { acao: AcaoLote; rotulo: string; dica: string; transmite?: boolean }[] = [
  { acao: 'recalcular', rotulo: 'Recalcular', dica: 'Refaz o cálculo com as notas de agora' },
  {
    acao: 'rascunho',
    rotulo: 'Rascunho no SERPRO',
    dica: 'Pede à Receita o cálculo dela (sem transmitir) e o RBT12 dela para comparar',
  },
  {
    acao: 'importar',
    rotulo: 'Importar da Receita',
    dica: 'Lê a declaração do mês anterior (apurando 09, lê a de 08): receita dos 12 meses do RBT12. Não transmite',
  },
  {
    acao: 'transmitir',
    rotulo: 'Transmitir aprovadas (original)',
    dica: 'Consulta a Receita antes: mês já declarado não é enviado (aí só Retificar, na empresa)',
    transmite: true,
  },
  {
    acao: 'documentos',
    rotulo: 'Gerar DAS e extrato',
    dica: 'Para as transmitidas que ainda não têm guia',
    transmite: true,
  },
];

const PASSOS: TourPasso[] = [
  {
    alvo: 'mes',
    titulo: 'Competência',
    texto: 'Escolha o mês de apuração. Abre sempre no mês anterior, que é o que vence dia 20.',
  },
  {
    alvo: 'kpis',
    titulo: 'Situação do mês',
    texto:
      'Cada número filtra a lista. O caminho é: calculada → conferência das pendências → aprovada → transmitida → concluída (DAS e extrato salvos).',
  },
  {
    alvo: 'aviso-serpro',
    titulo: 'SERPRO e certificado',
    texto:
      'Aqui aparece se a integração com a Receita está configurada e se o certificado do escritório está no cofre. Sem isso dá para calcular e aprovar, mas não transmitir.',
  },
  {
    alvo: 'recalculo',
    titulo: 'Recálculo automático',
    texto:
      'Ligado, o sistema recalcula todas as empresas toda noite às 03:30. Desligado (padrão), só calcula quando alguém clica em Calcular ou Ações → Recalcular.',
  },
  {
    alvo: 'busca',
    titulo: 'Busca',
    texto: 'Procure por nome, CNPJ ou ID. O filtro de responsável mostra só a sua carteira fiscal.',
  },
  {
    alvo: 'lote',
    titulo: 'Ações em lote',
    texto:
      'Valem para as empresas marcadas (ou todas, se nenhuma marcada). Transmitir só pega as aprovadas: nada sai sem conferência.',
  },
  {
    alvo: 'faturas',
    titulo: 'Faturas (receita sem nota)',
    texto:
      'Receita que não tem XML (fatura, locação, recibo) é lançada lá e entra no cálculo do mês. Não esqueça antes de aprovar.',
  },
  {
    alvo: 'card',
    titulo: 'A empresa',
    texto:
      'Nosso DAS ao lado do Domínio (só referência), do rascunho da Receita e do mês anterior. Vermelho = diferença.',
  },
  {
    alvo: 'parametros',
    titulo: 'Parâmetros',
    texto:
      'ISS fixo, ICMS fora do DAS, regras por item da LC 116 e filiais. Edita na aba Simples Nacional do cadastro.',
  },
  {
    alvo: 'situacao',
    titulo: 'Conferir e aprovar',
    texto:
      'Em "Conferir" estão o cálculo, a auditoria (aceite com justificativa; bloqueio precisa corrigir), os documentos com correção da segregação e os arquivos. Aprovam o responsável fiscal, a gestão, a diretoria e o admin.',
  },
];

function Kpi({
  kpi,
  totais,
  carregando,
  ativo,
  onClick,
}: {
  kpi: IKpi;
  totais?: ITotais;
  carregando: boolean;
  ativo: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip label={kpi.dica} fontSize="11px" hasArrow placement="top" openDelay={300}>
      <Box
        as="button"
        type="button"
        onClick={onClick}
        aria-pressed={ativo}
        textAlign="left"
        w="full"
        p="10px 12px"
        borderRadius="14px"
        border="1.5px solid"
        borderColor={ativo ? kpi.cor : 'rgba(15,23,42,0.07)'}
        bg={ativo ? `${kpi.cor}14` : 'rgba(255,255,255,0.65)'}
        transition="all 0.14s"
        _hover={{ borderColor: `${kpi.cor}66`, transform: 'translateY(-1px)' }}
        _focusVisible={{ outline: `2px solid ${kpi.cor}`, outlineOffset: '2px' }}
      >
        <Flex align="center" gap="10px">
          <Flex
            w="32px"
            h="32px"
            borderRadius="10px"
            align="center"
            justify="center"
            flex="none"
            bg={`${kpi.cor}1A`}
            color={kpi.cor}
          >
            {kpi.icone}
          </Flex>
          <Box minW={0}>
            <Text fontSize="10.5px" color="#64748B" fontWeight={600} lineHeight="1.25">
              {kpi.rotulo}
            </Text>
            {carregando ? (
              <Spinner size="xs" color={kpi.cor} mt={1} />
            ) : (
              <Text
                fontSize="18px"
                fontWeight={800}
                color="#0F172A"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {kpi.valor(totais).toLocaleString('pt-BR')}
              </Text>
            )}
          </Box>
        </Flex>
      </Box>
    </Tooltip>
  );
}

export function SupervisorSimplesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const podeTransmitir = hasPermission(TELA_PATH, 'transmitir');
  const podeConfigurar = hasPermission(TELA_PATH, 'configurar');
  const podeFaturas = hasPermission(TELA_FATURAS_PATH, 'view');
  const tour = useTour('supervisor-simples');

  const { currentDate, handlePrevMonth, handleNextMonth, handleSelectMonth, filters, setFilters } =
    useMonthFilter({ additionalParams: { situacao: '', search: '', responsavel: '' } });
  const competencia = currentDate.clone().startOf('month').format('YYYY-MM-DD');
  const situacao = filters.get('situacao') ?? '';
  const search = filters.get('search') ?? '';
  const responsavel = filters.get('responsavel') ?? '';

  const [textoBusca, setTextoBusca] = useState(search);
  const [textoResp, setTextoResp] = useState(responsavel);
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  const [aberta, setAberta] = useState<number | null>(null);
  const [manual, setManual] = useState(false);
  const [tarefa, setTarefa] = useState<{ id: string; acao: AcaoLote; total: number } | null>(null);

  const setFiltro = useCallback(
    (campo: string, valor: string) => {
      const novo = new URLSearchParams(filters);
      novo.set(campo, valor);
      novo.set('page', '1');
      setFilters(novo);
    },
    [filters, setFilters],
  );
  const buscar = useDebounceCallback((c: string, v: string) => setFiltro(c, v), 400);

  useEffect(() => setSelecionados(new Set()), [competencia, situacao, search, responsavel]);

  const { data: config } = useQuery({
    queryKey: ['sn-config'],
    queryFn: snApi.config,
    staleTime: 300000,
  });
  const { data: totais, isFetching: carregandoTotais } = useQuery({
    queryKey: ['sn-totais', competencia],
    queryFn: () => snApi.totais(competencia),
  });
  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['sn-lista', competencia, situacao, search, responsavel],
      queryFn: ({ pageParam }) =>
        snApi.listar({
          competencia,
          situacao: situacao || undefined,
          search: search || undefined,
          responsavel: responsavel || undefined,
          page: pageParam,
          size: TAMANHO,
        }),
      initialPageParam: 1,
      getNextPageParam: ultima => (ultima.page < ultima.pages ? ultima.page + 1 : undefined),
    });
  const itens = useMemo(() => data?.pages.flatMap(p => p.items) ?? [], [data]);
  const total = data?.pages[0]?.total ?? 0;

  const sentinela = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinela.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(e => {
      if (e[0].isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const recalculoAutomatico = useMutation({
    mutationFn: (ligado: boolean) => snApi.salvarRecalculoAutomatico(ligado),
    onSuccess: r => {
      toast.success(
        r.recalculo_automatico
          ? 'Recálculo automático ligado: roda toda noite às 03:30.'
          : 'Recálculo automático desligado.',
      );
      qc.invalidateQueries({ queryKey: ['sn-config'] });
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });

  const calcular = useMutation({
    mutationFn: (empresaId: number) => snApi.calcular(empresaId, competencia),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sn-lista'] });
      qc.invalidateQueries({ queryKey: ['sn-totais'] });
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });

  // --- lote ---------------------------------------------------------------------------
  const lote = useMutation({
    mutationFn: (v: { acao: AcaoLote; ids: number[] }) => snApi.lote(v.acao, competencia, v.ids),
    onSuccess: (r, v) => {
      setTarefa({ id: r.task_id, acao: v.acao, total: r.total });
      toast.info(`${r.total} empresa(s) na fila.`);
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });
  const { data: status } = useQuery<IStatusLote>({
    queryKey: ['sn-lote', tarefa?.id],
    queryFn: () => snApi.statusLote(tarefa!.id),
    enabled: !!tarefa,
    refetchInterval: q => {
      const s = q.state.data?.status;
      return s === 'completed' || s === 'failed' || s === 'not_found' ? false : 2000;
    },
  });
  const terminou = status?.status === 'completed' || status?.status === 'failed';
  useEffect(() => {
    if (!tarefa || !terminou) return;
    const res = status?.progresso?.resultados ?? [];
    const falhas = res.filter(r => !r.ok);
    if (status?.status === 'failed') toast.error(status.error || 'O lote parou com erro.');
    else if (falhas.length)
      toast.warning(`${res.length - falhas.length} ok, ${falhas.length} com erro.`);
    else toast.success(`Lote concluído: ${res.length} empresa(s).`);
    qc.invalidateQueries({ queryKey: ['sn-lista'] });
    qc.invalidateQueries({ queryKey: ['sn-totais'] });
    qc.invalidateQueries({ queryKey: ['sn-detalhe'] });
  }, [terminou]); // eslint-disable-line react-hooks/exhaustive-deps

  const disparar = (acao: AcaoLote, ids: number[]) => {
    if (tarefa && !terminou) {
      toast.warning('Espere o lote atual terminar.');
      return;
    }
    if (acao === 'transmitir') {
      const alvo = ids.length ? `${ids.length} empresa(s) marcada(s)` : 'todas as aprovadas';
      // transmissão é definitiva: pede confirmação
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Transmitir o PGDAS-D de ${alvo}? Depois disso, só por retificação.`))
        return;
    }
    if (acao === 'importar' || acao === 'rascunho') {
      const alvo = ids.length ? `${ids.length} empresa(s) marcada(s)` : 'todas as empresas do mês';
      // cada consulta ao SERPRO é cobrada
      if (
        // eslint-disable-next-line no-alert
        !window.confirm(
          `Consultar a Receita para ${alvo}? Nada é transmitido, mas cada consulta é cobrada pelo SERPRO.`,
        )
      )
        return;
    }
    lote.mutate({ acao, ids });
  };

  const feitas = Number(status?.progresso?.feitas ?? 0);
  const totalLote = Number(status?.progresso?.total ?? tarefa?.total ?? 0);
  const nomes = useMemo(
    () => Object.fromEntries(itens.map(i => [i.empresa.id, i.empresa.nome])),
    [itens],
  );
  const falhasLote = (status?.progresso?.resultados ?? []).filter(r => !r.ok);

  return (
    <GlassPageBg>
      <Flex direction="column" gap={5} px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        <Flex align="center" gap={3} wrap="wrap">
          <ReturnButton previousPage="modulesHome/supervisor" />
          <Flex
            w="46px"
            h="46px"
            borderRadius="14px"
            align="center"
            justify="center"
            flex="none"
            bgGradient="linear(135deg, #1B88F5, #6366F1)"
            color="white"
            boxShadow="0 8px 20px -6px rgba(99,102,241,0.45)"
          >
            <Calculator size={24} weight="duotone" />
          </Flex>
          <Box minW={0}>
            <Text
              fontSize="11px"
              letterSpacing="0.14em"
              textTransform="uppercase"
              color="#94A3B8"
              fontWeight={600}
            >
              Supervisor › Simples Nacional
            </Text>
            <Text fontSize="22px" fontWeight={700} letterSpacing="-0.02em" color="#0F172A">
              Simples Nacional
            </Text>
          </Box>
          <Box flex={1} />
          {(carregandoTotais || isFetching) && (
            <Flex role="status" aria-live="polite" align="center" gap={2}>
              <Spinner size="sm" color="#1B88F5" />
              <Text fontSize="13px" color="#64748B">
                Carregando…
              </Text>
            </Flex>
          )}
          {config && (
            <Tooltip
              label={
                podeConfigurar
                  ? 'Liga o recálculo de todas as empresas toda noite às 03:30 (mês anterior e atual). Desligado: só calcula quando alguém clica.'
                  : 'Só quem tem a permissão Configurar do Simples altera.'
              }
              fontSize="11px"
              hasArrow
              maxW="280px"
            >
              <Flex align="center" gap={2} data-tour="recalculo">
                <Switch
                  size="sm"
                  colorScheme="blue"
                  isChecked={config.recalculo_automatico}
                  isDisabled={!podeConfigurar || recalculoAutomatico.isPending}
                  onChange={e => recalculoAutomatico.mutate(e.target.checked)}
                  aria-label="Recálculo automático"
                />
                <Text fontSize="12px" color="#475569" fontWeight={600}>
                  Recálculo automático
                </Text>
              </Flex>
            </Tooltip>
          )}
          <Button size="sm" variant="ghost" leftIcon={<Question size={16} />} onClick={tour.abrir}>
            Como funciona
          </Button>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<BookOpen size={16} />}
            onClick={() => setManual(true)}
          >
            Motor de cálculo
          </Button>
          <Box data-tour="mes">
            <MonthNavigator
              currentDate={currentDate}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onSelectMonth={handleSelectMonth}
              compact
            />
          </Box>
        </Flex>

        {config && (
          <GlassCard intensity="strong" p="10px 14px" data-tour="aviso-serpro">
            <Flex align="center" gap={2} fontSize="13px">
              {config.pendencia ? (
                <>
                  <WarningCircle size={18} color="#F59E0B" weight="fill" />
                  <Text color="#92400E">
                    {config.pendencia} — dá para calcular e aprovar; transmitir fica bloqueado.
                  </Text>
                </>
              ) : (
                <>
                  <CheckCircle size={18} color="#22C55E" weight="fill" />
                  <Text color="#166534">
                    SERPRO ({config.ambiente}) ok · certificado {config.certificado?.titular} válido
                    até{' '}
                    {config.certificado?.valido_ate?.slice(0, 10).split('-').reverse().join('/')}
                  </Text>
                </>
              )}
            </Flex>
          </GlassCard>
        )}

        <GlassCard intensity="strong" p="14px 16px" data-tour="kpis">
          <Flex align="baseline" gap={3} mb={2} wrap="wrap">
            <Text
              fontSize="10.5px"
              fontWeight={700}
              color="#94A3B8"
              letterSpacing="0.12em"
              textTransform="uppercase"
            >
              Apuração do mês
            </Text>
            <Text fontSize="12px" color="#64748B">
              Receita {brl(totais?.receita)} · DAS {brl(totais?.valor_das)}
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 2, md: 3, xl: 5, '2xl': 9 }} spacing={2}>
            {KPIS.map(k => (
              <Kpi
                key={k.filtro || 'todas'}
                kpi={k}
                totais={totais}
                carregando={carregandoTotais && !totais}
                ativo={situacao === k.filtro}
                onClick={() => setFiltro('situacao', situacao === k.filtro ? '' : k.filtro)}
              />
            ))}
          </SimpleGrid>
        </GlassCard>

        <GlassCard intensity="strong" p="12px 14px" overflow="visible">
          <Flex gap={3} wrap="wrap" align="center">
            <InputGroup size="sm" w={{ base: 'full', md: '320px' }} data-tour="busca">
              <InputLeftElement pointerEvents="none">
                <MagnifyingGlass size={15} color="#94A3B8" />
              </InputLeftElement>
              <Input
                borderRadius="10px"
                placeholder="Nome, CNPJ ou ID"
                value={textoBusca}
                onChange={e => {
                  setTextoBusca(e.target.value);
                  buscar('search', e.target.value);
                }}
              />
            </InputGroup>
            <Input
              size="sm"
              borderRadius="10px"
              w={{ base: 'full', md: '220px' }}
              placeholder="Responsável fiscal"
              value={textoResp}
              onChange={e => {
                setTextoResp(e.target.value);
                buscar('responsavel', e.target.value);
              }}
            />
            <Text fontSize="12px" color="#64748B" fontWeight={600}>
              {total.toLocaleString('pt-BR')} {total === 1 ? 'empresa' : 'empresas'}
              {selecionados.size > 0 && ` · ${selecionados.size} marcada(s)`}
            </Text>
            <Box flex={1} />
            {itens.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setSelecionados(
                    selecionados.size === itens.length
                      ? new Set()
                      : new Set(itens.map(i => i.empresa.id)),
                  )
                }
              >
                {selecionados.size === itens.length ? 'Desmarcar' : 'Marcar visíveis'}
              </Button>
            )}
            {podeFaturas && (
              <Button
                size="sm"
                variant="outline"
                borderRadius="10px"
                leftIcon={<FileText size={15} />}
                onClick={() =>
                  navigate(
                    `${TELA_FATURAS_PATH}?date_start=${competencia}&date_end=${currentDate
                      .clone()
                      .endOf('month')
                      .format('YYYY-MM-DD')}`,
                  )
                }
                data-tour="faturas"
              >
                Faturas
              </Button>
            )}
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                colorScheme="blue"
                borderRadius="10px"
                rightIcon={<CaretDown size={14} />}
                isLoading={lote.isPending}
                data-tour="lote"
              >
                Ações {selecionados.size ? `(${selecionados.size})` : '(todas)'}
              </MenuButton>
              <MenuList fontSize="13px">
                {ACOES_LOTE.filter(a => !a.transmite || podeTransmitir).map(a => (
                  <MenuItem
                    key={a.acao}
                    onClick={() => disparar(a.acao, Array.from(selecionados))}
                    isDisabled={a.acao !== 'recalcular' && !!config?.pendencia}
                  >
                    <Box>
                      <Text fontWeight={600}>{a.rotulo}</Text>
                      <Text fontSize="11px" color="#64748B">
                        {a.dica}
                      </Text>
                    </Box>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>
          {tarefa && (
            <Box mt={3}>
              <Flex justify="space-between" fontSize="12px" color="#475569" mb={1}>
                <Text>
                  {ACOES_LOTE.find(a => a.acao === tarefa.acao)?.rotulo}: {feitas}/{totalLote}
                  {terminou ? ' — terminou' : ''}
                </Text>
                {terminou && (
                  <Button size="xs" variant="ghost" onClick={() => setTarefa(null)}>
                    Fechar
                  </Button>
                )}
              </Flex>
              <Progress
                size="sm"
                borderRadius="full"
                value={totalLote ? (feitas / totalLote) * 100 : 0}
                isIndeterminate={!terminou && !feitas}
                colorScheme={falhasLote.length ? 'orange' : 'blue'}
              />
              {falhasLote.length > 0 && (
                <Box mt={2} maxH="140px" overflowY="auto" fontSize="12px" color="#B91C1C">
                  {falhasLote.map(f => (
                    <Text key={f.empresa_id}>
                      {nomes[f.empresa_id] ?? `Empresa ${f.empresa_id}`}: {f.erro}
                    </Text>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </GlassCard>

        {isLoading && (
          <Flex direction="column" align="center" gap={3} py={12}>
            <Spinner size="lg" color="#1B88F5" thickness="3px" />
            <Text fontSize="13px" color="#64748B">
              Carregando empresas…
            </Text>
          </Flex>
        )}
        {!isLoading && itens.length === 0 && (
          <GlassCard intensity="medium" p={10}>
            <Flex direction="column" align="center" gap={2}>
              <Buildings size={40} color="#CBD5E1" />
              <Text fontSize="13px" color="#64748B">
                Nenhuma empresa encontrada.
              </Text>
            </Flex>
          </GlassCard>
        )}
        <Flex direction="column" gap={3}>
          {itens.map((item, i) => (
            <ApuracaoCard
              key={item.empresa.id}
              item={item}
              primeiro={i === 0}
              selecionado={selecionados.has(item.empresa.id)}
              onSelecionar={v =>
                setSelecionados(atual => {
                  const novo = new Set(atual);
                  if (v) novo.add(item.empresa.id);
                  else novo.delete(item.empresa.id);
                  return novo;
                })
              }
              onAbrir={() => setAberta(item.empresa.id)}
              onCalcular={() => calcular.mutate(item.empresa.id)}
              calculando={calcular.isPending && calcular.variables === item.empresa.id}
            />
          ))}
        </Flex>
        <div ref={sentinela} className="h-10 w-full" />
        {isFetchingNextPage && (
          <Flex align="center" justify="center" py={4} gap={3}>
            <Spinner size="md" color="#1B88F5" />
          </Flex>
        )}
      </Flex>

      <ApuracaoDrawer
        empresaId={aberta}
        competencia={competencia}
        onFechar={() => setAberta(null)}
        onLote={disparar}
        podeTransmitir={podeTransmitir}
      />
      <Tour passos={PASSOS} aberto={tour.aberto} onFechar={tour.fechar} />
      <MotorCalculoDrawer aberto={manual} onFechar={() => setManual(false)} />
    </GlassPageBg>
  );
}
